import React, { useMemo, useRef } from 'react';
import type {
  Accessor,
  Cell,
  Column,
  Row,
  TableInstance,
  UseRowSelectRowProps,
  UseSortByColumnOptions
} from 'react-table';
import type { ValidIconName } from './Icon';
import { TableElement, TablePagination } from './internal';
import { Button } from './Button';
import type { InfiniteScrollStore } from '../../lib';
import { intl } from '../../lib';
import { Maskable } from './LoadMask';
import type { ClassNameProp } from '../common-types';
import type { InfiniteScrollProps } from './InfiniteScroll';
import { InfiniteScroll } from './InfiniteScroll';
import type { UseStore } from 'zustand';

// react-table does `T extends object` in their types, so this is unfortunately needed, at least for now
// eslint-disable-next-line @typescript-eslint/ban-types
type DataType = object;

export type CustomRow<RowDataType extends DataType> = Row<RowDataType> &
  // partial because the row selection plugin is not guaranteed to always be present
  Partial<UseRowSelectRowProps<RowDataType>> & {
    original: RowDataType;
    cells: CustomCell<RowDataType>[];
  };

export type CustomColumn<RowDataType extends DataType> = Column<RowDataType> &
  Partial<UseSortByColumnOptions<RowDataType>> & {
    breakOrder?: number;
    accessor: keyof RowDataType | Accessor<RowDataType>;
    /**
     * If hiding this column should be disabled, when the `hideableColumns` option is enabled on the table.
     */
    disableHideColumn?: boolean;
    /**
     * Id of the column's state saved in the database.
     */
    stateId?: string;
    /**
     * States that should be persisted.
     */
    persistState?: Array<keyof PersistentColumnState>;
  };

/**
 * Type for the table's columns' state which is persisted using the persistentStateProvider.
 * @author agd
 */
export type PersistentColumnState = {
  /** If the column is hidden. */
  hidden?: boolean;
};

export type CustomCell<RowDataType extends DataType> = Cell<RowDataType> & {
  column: CustomColumn<RowDataType>;
};

export type TableAction<RowDataType extends DataType> = {
  icon: ValidIconName;
  onClick: (row: CustomRow<RowDataType>) => void;
  disabled?: boolean | ((row: CustomRow<RowDataType>) => boolean);
  label?: string;
  /** Render the action button only if renderCondition is true */
  renderCondition?: boolean | ((row: CustomRow<RowDataType>) => boolean);
  confirmation?: {
    message?: string;
    title?: string;
  };
};

export type TableProps<
  RowDataType extends DataType,
  Params = unknown
> = ClassNameProp & {
  /** Function to handle row click */
  onRowClick?: (row: CustomRow<RowDataType>) => void;
  /** Function to handle row dblclick */
  onRowDoubleClick?: (row: CustomRow<RowDataType>) => void;
  /**
   * Enable client-side sorting for all columns.
   * When setting this to true, you also need to add the `useSortBy` plugin to your
   * tableInstance.
   */
  sortable?: boolean;
  /**
   * Paging style.
   */
  paging?: 'none' | 'pages' | 'infinite-scroll';
  /** Enable option to hide columns of the table */
  hideableColumns?: boolean;
  /** The message shown if the data is empty array */
  noDataMessage?: string;
  /** The icon shown if the data is empty array */
  noDataIcon?: ValidIconName;
  /** The breakpoint when the table should be shown in the responsive view */
  breakpoint?: 'sm' | 'md' | 'lg' | 'xl';
  /** Option to show rows with a little less padding */
  condensed?: boolean;
  /** Actions for each of rows */
  actions?: TableAction<RowDataType>[];
  /** If set to true, actions are hidden under single button */
  squashActions?: boolean;
  /** ID for the scroll area container.
   * This property is mandatory if the infinite scroll is enabled.
   * */
  scrollTargetId?: string;
  /** The store instance used to fetch data dynamically
   * - store controls the server-side pagination
   * */
  store?: UseStore<InfiniteScrollStore<DataType, any>>;
  /**
   * The tableInstance created by the 'react-table' library.
   * This is the most important piece of information for the Table to work properly.
   */
  tableInstance: TableInstance<RowDataType>;
  /**
   * Method for loading the next page when in 'infinite-scroll' paging style.
   */
  loadNextPage?: InfiniteScrollProps<RowDataType, Params>['next'];
  /**
   * If the "scroll to top" button should be hidden or not.
   * The "scroll to top" button is only present in "infinite-scroll" paging mode, so outside of that mode this option is useless.
   */
  hideScrollToTop?: boolean;
  /**
   * The caption of a table.
   * It is used for the screen reader and therefore should be descriptive.
   * Please note that if language keys are required, intl(captionString) should be passed here.
   */
  caption: string;
};

const defaultScrollTargetId = 'default-scroll-target-id';

export function Table<RowDataType extends DataType>(
  props: TableProps<RowDataType> & {
    paging: 'none' | 'pages';
    store?: undefined;
    loadNextPage?: undefined;
    scrollTargetId?: undefined;
    caption: string;
  }
): JSX.Element;
export function Table<RowDataType extends DataType, Params>(
  props: TableProps<RowDataType, Params> & {
    paging: 'infinite-scroll';
    store: UseStore<InfiniteScrollStore<DataType, any>>;
    loadNextPage: InfiniteScrollProps<RowDataType, Params>['next'];
    scrollTargetId: string;
    caption: string;
  }
): JSX.Element;
export function Table<RowDataType extends DataType, Params = unknown>({
  store: storeHook,
  onRowClick = undefined,
  onRowDoubleClick = undefined,
  sortable = false,
  paging = 'none',
  hideableColumns = false,
  breakpoint,
  noDataMessage = intl('common.noData'),
  noDataIcon = 'info',
  condensed = false,
  actions = [],
  className = '',
  squashActions = false,
  scrollTargetId = defaultScrollTargetId,
  tableInstance,
  loadNextPage,
  hideScrollToTop = false,
  caption
}: TableProps<RowDataType, Params>): JSX.Element {
  const store = storeHook?.();
  if (paging === 'infinite-scroll' && !store) {
    console.warn(
      'A store needs to be present for an infinitely scrolling Table to work. Disabling paging ...'
    );
  }
  const ref = useRef<HTMLDivElement>(null);
  const {
    rows,
    page,
    state: { pageSize }
  } = tableInstance as any;

  const tableClassName = useMemo(() => {
    const classNames: string[] = [];

    if (breakpoint) {
      classNames.push(`responsive-${breakpoint}`);
    }

    if (condensed) {
      classNames.push('condensed');
    }

    return classNames.join(' ');
  }, [breakpoint, condensed]);

  /** Function to get data to show considering the pagination presence */
  const getTableData = () => {
    if (paging === 'pages') {
      return page;
    }

    return rows;
  };

  const tableElement = (
    <TableElement
      className={tableClassName}
      tableInstance={tableInstance}
      showBlankState={paging !== 'infinite-scroll'}
      actions={actions}
      breakpoint={breakpoint}
      sortable={sortable}
      data={getTableData()}
      noDataIcon={noDataIcon}
      noDataMessage={noDataMessage}
      onRowClick={onRowClick}
      onRowDoubleClick={onRowDoubleClick}
      squashActions={squashActions}
      hideableColumns={hideableColumns}
      paging={paging}
      caption={caption}
    />
  );

  return (
    <>
      <div className={`ehs-table-wrapper ${className}`.trim()} ref={ref}>
        {paging === 'infinite-scroll' && store ? (
          <>
            <InfiniteScroll
              scrollTargetId={scrollTargetId}
              store={store}
              pageSize={pageSize}
              next={() => loadNextPage?.()}
              style={{
                // remove default overflow on the infinite-scroll-component, which messes with
                // the sticky header
                overflow: 'unset'
              }}
            >
              {tableElement}
            </InfiniteScroll>
            {store.loadedCount > 0 && !hideScrollToTop && (
              <Button
                icon="arrow-up"
                onClick={() => {
                  const scrollArea = document.getElementById(scrollTargetId);
                  if (scrollArea) {
                    scrollArea.scrollTop = 0;
                  }
                }}
                variant="primary"
                className="scroll-to-top"
                text={intl('view.table.scrollToTop')}
              />
            )}
          </>
        ) : (
          <>
            <Maskable masked={store?.loading || false}>{tableElement}</Maskable>
            {paging === 'pages' && (
              <TablePagination tableInstance={tableInstance} />
            )}
          </>
        )}
      </div>
    </>
  );
}
